/**
 * DefencIQ PDF Report Generator
 * Generates professional PDF reports from assessment data
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SECTION_CONFIG, COMPONENT_LABELS, LAYER_LABELS } from '@/config/domain-weights';
import { SECTION_GROUPS, ENHANCED_SCORE_BANDS } from '@/lib/scoring-engine';
import { SECTION_ORDER } from '@/config/question-config';
import { renderHybridSections } from '@/lib/pdf-hybrid-sections';
import { resolveEnabledSections, SECTION_KEYS, DEFAULT_PRESET_ID } from '@/lib/pdf-presets';
import {
  renderCover,
  renderEvidenceReadinessSection,
  renderDispGapAnalysis,
  renderTechnologyStack,
  renderRecommendedNextStep,
  renderTechnicalSummary,
  renderCsqSubmissionMapping,
  renderAssessorSignature,
  renderAuditorMetadata,
} from '@/lib/pdf-sections';

// Color palette
const COLORS = {
  primary: [15, 23, 42],      // slate-900
  secondary: [71, 85, 105],   // slate-500
  accent: [79, 70, 229],      // indigo-600
  success: [16, 185, 129],    // emerald-500
  warning: [245, 158, 11],    // amber-500
  danger: [239, 68, 68],      // red-500
  critical: [190, 18, 60],    // rose-700
  light: [248, 250, 252],     // slate-50
  white: [255, 255, 255],
  border: [226, 232, 240],    // slate-200
};

/**
 * Get color based on score
 */
const getScoreColor = (score) => {
  if (score >= 85) return COLORS.success;
  if (score >= 65) return [14, 165, 233]; // sky-500
  if (score >= 40) return COLORS.warning;
  return COLORS.danger;
};

/**
 * Get priority color
 */
const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical': return COLORS.danger;
    case 'high': return COLORS.warning;
    case 'medium': return [14, 165, 233];
    default: return COLORS.secondary;
  }
};

/**
 * Get color for enhanced score band
 */
const getEnhancedBandColor = (score) => {
  if (score >= 85) return COLORS.success;      // ML2 Ready
  if (score >= 70) return [234, 179, 8];       // Near Ready (yellow)
  if (score >= 50) return [249, 115, 22];      // Developing (orange)
  if (score >= 30) return COLORS.danger;       // High Risk (red)
  return COLORS.critical;                       // Critical (dark red)
};

/**
 * Get flag severity color
 */
const getFlagSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return COLORS.critical;
    case 'high': return [249, 115, 22]; // orange
    case 'medium': return [234, 179, 8]; // yellow
    default: return COLORS.secondary;
  }
};

/**
 * Generate PDF report from assessment data
 * @param {Object} report - Generated report from report-generator.js
 * @param {Object} engagement - Engagement details
 * @returns {jsPDF} PDF document
 */
export const generatePDFReport = (report, engagement, opts = {}) => {
  const enabled = resolveEnabledSections(opts.preset || DEFAULT_PRESET_ID);
  const isOn = (key) => enabled.has(key);

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = margin;

  // ===== ORCHESTRATOR - compose retained sections in audience-preset order =====
  // ctx is a shared, mutable frame passed to each section renderer. Each
  // renderer returns the updated yPos so the next section can continue from
  // where the previous one ended.
  const ctx = { COLORS, margin, contentWidth, pageWidth, pageHeight, yPos };

  // Cover / Decision View - always on (never gated by preset)
  ctx.yPos = renderCover(doc, ctx, report, engagement);

  // Auditor Metadata Overlay - Auditor/Assessor preset only; renders as page 2
  // BEFORE Executive Snapshot so it becomes the auditor's context sheet.
  if (isOn(SECTION_KEYS.AUDITOR_METADATA)) {
    ctx.yPos = renderAuditorMetadata(doc, ctx, report, engagement);
  }

  // Hybrid part A: Executive Snapshot, Commercial Impact, How to Read, Gating
  const hybridReport = report._report_metadata?.hybrid_report;
  if (hybridReport) {
    const partAOrder = [];
    if (isOn(SECTION_KEYS.EXECUTIVE_SNAPSHOT)) partAOrder.push('exec_snapshot');
    if (isOn(SECTION_KEYS.COMMERCIAL_IMPACT))  partAOrder.push('commercial_impact');
    if (isOn(SECTION_KEYS.HOW_TO_READ))        partAOrder.push('how_to_read');
    if (isOn(SECTION_KEYS.GATING_ANALYSIS))    partAOrder.push('gating_analysis');
    if (partAOrder.length > 0) {
      ctx.yPos = renderHybridSections(doc, hybridReport, { ...ctx }, { only: partAOrder });
    }
  }

  if (isOn(SECTION_KEYS.EVIDENCE_READINESS)) {
    ctx.yPos = renderEvidenceReadinessSection(doc, ctx, report);
  }

  if (isOn(SECTION_KEYS.DISP_GAP_ANALYSIS)) {
    ctx.yPos = renderDispGapAnalysis(doc, ctx, report);
  }

  if (isOn(SECTION_KEYS.TECHNICAL_SUMMARY)) {
    ctx.yPos = renderTechnicalSummary(doc, ctx, report);
  }

  if (isOn(SECTION_KEYS.CSQ_SUBMISSION_MAPPING)) {
    ctx.yPos = renderCsqSubmissionMapping(doc, ctx, report);
  }

  if (isOn(SECTION_KEYS.TECHNOLOGY_STACK_STATUS)) {
    ctx.yPos = renderTechnologyStack(doc, ctx, report);
  }

  // Hybrid part B: Path to DISP Readiness (Remediation Roadmap)
  if (isOn(SECTION_KEYS.PATH_TO_DISP_READINESS) && hybridReport) {
    ctx.yPos = renderHybridSections(doc, hybridReport, { ...ctx }, { only: ['remediation_roadmap'] });
  }

  // Recommended Next Step - always on (never gated)
  ctx.yPos = renderRecommendedNextStep(doc, ctx, report, engagement);

  // Assessor Attestation - Auditor/Assessor preset only; final page for sign-off
  if (isOn(SECTION_KEYS.ASSESSOR_SIGNATURE)) {
    ctx.yPos = renderAssessorSignature(doc, ctx, report, engagement);
  }
  yPos = ctx.yPos;

  // ===== FOOTER ON ALL PAGES (skip page 1 - cover has its own branded footer) =====
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.secondary);
    doc.text(
      `DefencIQ Discovery Assessment | ${engagement.client_name || 'Client'} | Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  return doc;
};

/**
 * Export report as PDF file
 * @param {Object} report - Generated report
 * @param {Object} engagement - Engagement details
 */
export const exportPDF = (report, engagement, opts = {}) => {
  const doc = generatePDFReport(report, engagement, opts);
  const clientName = (engagement.client_name || 'client')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .substring(0, 30);
  const date = new Date().toISOString().split('T')[0];
  // Auditor preset gets a distinguishing suffix so signed copies are easy to
  // identify in a shared file store.
  const presetSuffix = opts.preset === 'auditor_assessor' ? '-auditor' : '';
  const filename = `defenciq-discovery-${clientName}${presetSuffix}-${date}.pdf`;
  doc.save(filename);
  return filename;
};

// ============================================================================
// EXECUTIVE SNAPSHOT PDF (2-page business-focused summary)
// ============================================================================

/**
 * Generate 2-page Executive Snapshot PDF
 * @param {Object} payload - From buildExecutiveSnapshotPayload()
 * @returns {jsPDF} doc
 */
const generateExecutiveSnapshotPDF = (payload) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = margin;

  // ===== PAGE 1 =====

  // Dark header bar
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 44, 'F');

  doc.setTextColor(...COLORS.white);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Executive Snapshot', margin, 18);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Business Summary for ${payload.client_name}`, margin, 27);

  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  const headerRight = `${payload.assessment_date}${payload.assessor_name ? '  |  ' + payload.assessor_name : ''}`;
  doc.text(headerRight, pageWidth - margin, 27, { align: 'right' });

  // Readiness headline
  yPos = 52;
  const isReady = payload.readiness_label === 'Ready';
  const isPartial = payload.readiness_label === 'Partially Ready';
  const headlineColor = isReady ? COLORS.success : isPartial ? COLORS.warning : COLORS.danger;
  const headline = isReady ? 'You Are Approaching DISP Readiness' : 'You Are Not Ready for DISP';
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  const hlW = Math.min(doc.getTextWidth(headline) + 16, contentWidth);
  doc.setFillColor(...headlineColor);
  doc.roundedRect(margin, yPos, hlW, 10, 3, 3, 'F');
  doc.setTextColor(...COLORS.white);
  doc.text(headline, margin + 8, yPos + 7);
  yPos += 18;

  // 5 Summary Cards (added Evidence Readiness alongside ML2 so boards see both dimensions)
  const era = payload.evidence_readiness_assessment;
  const eraColor = !era?.assessed ? COLORS.secondary
    : era.band?.color === 'emerald' ? COLORS.success
    : era.band?.color === 'sky' ? [14, 165, 233]
    : era.band?.color === 'amber' ? COLORS.warning
    : COLORS.danger;
  const cardW = (contentWidth - 12) / 5;
  const cards = [
    { label: 'ML2 Maturity', value: `${payload.weighted_ml2_score}%`, sub: payload.score_band, color: getScoreColor(payload.weighted_ml2_score) },
    { label: 'Evidence Readiness', value: era?.assessed ? `${era.overall_percent}%` : 'N/A', sub: era?.assessed ? era.band.label : 'Not Assessed', color: eraColor },
    { label: 'DISP Readiness', value: payload.readiness_label || 'Not Ready', sub: '', color: isReady ? COLORS.success : isPartial ? COLORS.warning : COLORS.danger },
    { label: 'Time to Ready', value: payload.time_to_readiness, sub: `Confidence: ${payload.timeline_confidence || 'Medium'}`, color: COLORS.primary },
    { label: 'Audit Risk', value: payload.audit_failure_risk, sub: '', color: payload.audit_failure_risk === 'CRITICAL' ? COLORS.danger : payload.audit_failure_risk === 'HIGH' ? [249, 115, 22] : payload.audit_failure_risk === 'MODERATE' ? COLORS.warning : COLORS.success }
  ];

  cards.forEach((card, i) => {
    const x = margin + i * (cardW + 3);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, yPos, cardW, 22, 2, 2, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(x, yPos, cardW, 22, 2, 2, 'S');

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    doc.text(card.label, x + cardW / 2, yPos + 5, { align: 'center' });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...card.color);
    doc.text(String(card.value), x + cardW / 2, yPos + 13, { align: 'center' });

    if (card.sub) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.secondary);
      doc.text(card.sub, x + cardW / 2, yPos + 18, { align: 'center' });
    }
  });
  yPos += 30;

  // Current Position
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Current Position', margin, yPos);
  yPos += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  const posText = `${payload.client_name} currently sits at ${payload.weighted_ml2_score}% maturity, assessed as ${payload.score_band}. Based on the current control environment, the organisation is ${payload.readiness_status.toLowerCase()}.`;
  const posLines = doc.splitTextToSize(posText, contentWidth - 4);
  doc.text(posLines, margin + 2, yPos);
  yPos += posLines.length * 4.5 + 4;

  // ===== SUBMISSION BLOCKERS (NEW) =====
  if (payload.submission_blockers?.length > 0) {
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Submission Blockers', margin, yPos);
    yPos += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    payload.submission_blockers.forEach(b => {
      doc.setFillColor(...COLORS.danger);
      doc.circle(margin + 4, yPos - 1, 1.2, 'F');
      const lines = doc.splitTextToSize(b, contentWidth - 12);
      lines.forEach((line, li) => doc.text(line, margin + 9, yPos + li * 4));
      yPos += lines.length * 4 + 2;
    });
    yPos += 2;
  }

  // ===== GATING CONTROL STATUS (NEW) =====
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Gating Control Status', margin, yPos);
  yPos += 6;

  // Single-line summary chip
  const gs = payload.gating_summary || { pass: 0, partial: 0, fail: 0 };
  const chipPad = 3;
  let chipX = margin;
  const drawChip = (label, value, color) => {
    const text = `${value} ${label}`;
    const w = doc.getTextWidth(text) + chipPad * 2;
    doc.setFillColor(...color);
    doc.roundedRect(chipX, yPos - 4, w, 6, 1.5, 1.5, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(text, chipX + chipPad, yPos);
    chipX += w + 4;
  };
  drawChip('Passed', gs.pass, COLORS.success);
  drawChip('Partial', gs.partial, COLORS.warning);
  drawChip('Failed', gs.fail, COLORS.danger);
  yPos += 6;

  // Insight line
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.secondary);
  const insightLines = doc.splitTextToSize(payload.gating_insight || '', contentWidth - 4);
  insightLines.forEach((line, li) => doc.text(line, margin + 2, yPos + li * 4));
  yPos += insightLines.length * 4 + 6;

  // ===== EVIDENCE READINESS (NEW) =====
  if (payload.evidence_readiness) {
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Evidence Readiness', margin, yPos);

    // Inline maturity badge
    const lvl = payload.evidence_readiness.level || 'Low';
    const lvlColor =
      lvl === 'High' ? COLORS.success :
      lvl === 'Medium' ? COLORS.warning :
      COLORS.danger;
    const labelText = lvl;
    const labelW = doc.getTextWidth(labelText) + 6;
    doc.setFillColor(...lvlColor);
    doc.roundedRect(margin + 50, yPos - 4, labelW, 6, 1.5, 1.5, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(labelText, margin + 53, yPos);
    yPos += 6;

    // 1-2 sentence advisory
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    const erLines = doc.splitTextToSize(payload.evidence_readiness.explanation || '', contentWidth - 4);
    erLines.forEach((line, li) => doc.text(line, margin + 2, yPos + li * 4));
    yPos += erLines.length * 4 + 6;
  }

  // Maturity highlights
  if (payload.weakest_domains.length > 0 || payload.strongest_domains.length > 0) {
    const halfW = (contentWidth - 6) / 2;

    // Weakest
    const weakH = 8 + payload.weakest_domains.length * 4.5;
    doc.setFillColor(254, 242, 242);
    doc.roundedRect(margin, yPos, halfW, weakH, 2, 2, 'F');
    doc.setDrawColor(252, 165, 165);
    doc.roundedRect(margin, yPos, halfW, weakH, 2, 2, 'S');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.danger);
    doc.text('Weakest Areas', margin + 3, yPos + 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    payload.weakest_domains.forEach((d, i) => {
      doc.setTextColor(...COLORS.secondary);
      doc.text(`${d.name} (${d.score}%)`, margin + 5, yPos + 8.5 + i * 4.5);
    });

    // Strongest
    const rx = margin + halfW + 6;
    const strongH = 8 + payload.strongest_domains.length * 4.5;
    doc.setFillColor(240, 253, 244);
    doc.roundedRect(rx, yPos, halfW, strongH, 2, 2, 'F');
    doc.setDrawColor(134, 239, 172);
    doc.roundedRect(rx, yPos, halfW, strongH, 2, 2, 'S');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.success);
    doc.text('Strongest Areas', rx + 3, yPos + 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    payload.strongest_domains.forEach((d, i) => {
      doc.setTextColor(...COLORS.secondary);
      doc.text(`${d.name} (${d.score}%)`, rx + 5, yPos + 8.5 + i * 4.5);
    });
    yPos += Math.max(weakH, strongH) + 6;
  }

  // ===== COMMERCIAL IMPACT (replaces Business Risk) =====
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Commercial Impact', margin, yPos);
  yPos += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  (payload.commercial_impact || []).forEach(c => {
    doc.setFillColor(...COLORS.danger);
    doc.circle(margin + 4, yPos - 1, 1, 'F');
    const lines = doc.splitTextToSize(c, contentWidth - 12);
    lines.forEach((line, li) => doc.text(line, margin + 9, yPos + li * 4.5));
    yPos += lines.length * 4.5 + 1.5;
  });
  yPos += 2;

  // Risk sentence
  const riskColor = payload.audit_failure_risk === 'CRITICAL' ? COLORS.danger
    : payload.audit_failure_risk === 'HIGH' ? [249, 115, 22]
    : payload.audit_failure_risk === 'MODERATE' ? COLORS.warning : COLORS.success;
  doc.setFillColor(...riskColor);
  doc.roundedRect(margin, yPos, contentWidth, 8, 2, 2, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(payload.risk_sentence, margin + 5, yPos + 5.5);
  yPos += 14;

  // What's Holding You Back
  if (payload.top_blockers.length > 0) {
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text("What's Holding You Back", margin, yPos);
    yPos += 7;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    payload.top_blockers.forEach(b => {
      doc.setFillColor(239, 68, 68);
      doc.circle(margin + 4, yPos - 1, 1.2, 'F');
      const bLines = doc.splitTextToSize(b, contentWidth - 12);
      bLines.forEach((line, li) => {
        doc.text(line, margin + 9, yPos + li * 4);
      });
      yPos += bLines.length * 4 + 2;
    });
  }

  // ===== PAGE 2 =====
  doc.addPage();
  yPos = margin;

  // Page 2 header stripe
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 14, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Executive Snapshot', margin, 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text(payload.client_name, pageWidth - margin, 9, { align: 'right' });
  yPos = 22;

  // What Needs to Be Done
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('What Needs to Be Done', margin, yPos);
  yPos += 8;

  const actionGroups = [
    { title: 'Critical First Steps (Gating Controls)', items: payload.priority_actions.critical, color: COLORS.danger, bgColor: [254, 242, 242], borderColor: [252, 165, 165] },
    { title: 'Next Phase (ML2 Uplift)', items: payload.priority_actions.uplift, color: [249, 115, 22], bgColor: [255, 247, 237], borderColor: [253, 186, 116] },
  ];

  actionGroups.forEach(group => {
    if (!group.items || group.items.length === 0) return;

    // Pre-wrap each item to fit within the panel; bullet sits at margin+6, text at margin+10
    // Right-edge of text region is (margin + contentWidth - 4) so usable width = contentWidth - 14
    const wrappedItems = group.items.map(item => doc.splitTextToSize(item, contentWidth - 14));
    const totalLines = wrappedItems.reduce((sum, lines) => sum + lines.length, 0);
    const lineH = 4.5;
    const blockH = 10 + totalLines * lineH + (group.items.length - 1) * 1.5;

    doc.setFillColor(...group.bgColor);
    doc.roundedRect(margin, yPos, contentWidth, blockH, 2, 2, 'F');
    doc.setDrawColor(...group.borderColor);
    doc.roundedRect(margin, yPos, contentWidth, blockH, 2, 2, 'S');

    doc.setTextColor(...group.color);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(group.title, margin + 4, yPos + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.secondary);

    let cursorY = yPos + 10;
    wrappedItems.forEach(lines => {
      // Draw bullet aligned with first line
      doc.setFillColor(...group.color);
      doc.circle(margin + 6, cursorY - 0.8, 0.8, 'F');
      lines.forEach((line, li) => {
        doc.text(line, margin + 10, cursorY + li * lineH);
      });
      cursorY += lines.length * lineH + 1.5;
    });

    yPos += blockH + 4;
  });
  yPos += 2;

  // Why DefencIQ
  doc.setFillColor(15, 23, 42);
  doc.roundedRect(margin, yPos, contentWidth, 6 + payload.defenciq_value_points.length * 5.5, 2, 2, 'F');
  const dvpBlockTop = yPos;

  doc.setTextColor(...COLORS.white);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Why DefencIQ', margin + 5, yPos + 5);
  yPos += 10;

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  payload.defenciq_value_points.forEach(point => {
    doc.setFillColor(79, 70, 229);
    doc.circle(margin + 7, yPos - 0.5, 1, 'F');
    doc.setTextColor(226, 232, 240);
    doc.text(point, margin + 12, yPos);
    yPos += 5.5;
  });

  yPos = dvpBlockTop + 6 + payload.defenciq_value_points.length * 5.5 + 8;

  // Recommended Next Step
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Recommended Next Step', margin, yPos);
  yPos += 7;

  doc.setFillColor(79, 70, 229);
  const svcText = `Recommended engagement: ${payload.recommended_service}`;
  const svcW = Math.min(doc.getTextWidth(svcText) + 14, contentWidth);
  doc.roundedRect(margin, yPos, svcW, 8, 2, 2, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(svcText, margin + 7, yPos + 5.5);
  yPos += 13;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  const closingLines = doc.splitTextToSize(payload.closing_recommendation, contentWidth - 4);
  doc.text(closingLines, margin + 2, yPos);
  yPos += closingLines.length * 4.5 + 8;

  // Closing CTA
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, yPos, contentWidth, 12, 2, 2, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, yPos, contentWidth, 12, 2, 2, 'S');
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('DefencIQ helps organisations move from partial implementation to defensible DISP readiness.', margin + 5, yPos + 7);
  yPos += 18;

  // Disclaimer
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text('Designed for executive decision-making - detailed technical findings are available in the full Discovery Report.', pageWidth / 2, yPos, { align: 'center' });

  // Footer on both pages
  for (let i = 1; i <= doc.internal.getNumberOfPages(); i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `DefencIQ Executive Snapshot | ${payload.client_name} | ${payload.assessment_date}`,
      pageWidth / 2, pageHeight - 8, { align: 'center' }
    );
  }

  return doc;
};

/**
 * Export Executive Snapshot as PDF
 */
export const exportExecutiveSnapshotPDF = (payload) => {
  const doc = generateExecutiveSnapshotPDF(payload);
  const clientName = (payload.client_name || 'client')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .substring(0, 30);
  const date = payload.assessment_date || new Date().toISOString().split('T')[0];
  const filename = `defenciq-snapshot-${clientName}-${date}.pdf`;
  doc.save(filename);
  return filename;
};

/**
 * Generate PDF comparison report for two assessments
 * @param {Object} a1 - First assessment { client_name, user_name, created_at, status }
 * @param {Object} a2 - Second assessment
 * @param {Object} s1 - First scores { finalScore, groupScores, domainScores, auditRisk, band, reasons }
 * @param {Object} s2 - Second scores
 */
export const generateComparisonPDF = (a1, a2, s1, s2) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  const colWidth = (contentWidth - 10) / 2;
  let yPos = margin;

  const addNewPageIfNeeded = (space = 30) => {
    if (yPos + space > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  const drawLine = (y) => {
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
  };

  const drawBar = (x, y, w, score, color) => {
    doc.setFillColor(226, 232, 240);
    doc.roundedRect(x, y, w, 4, 2, 2, 'F');
    if (score > 0) {
      doc.setFillColor(...color);
      doc.roundedRect(x, y, Math.max(2, w * (score / 100)), 4, 2, 2, 'F');
    }
  };

  const getRiskColor = (risk) => {
    const r = (risk || '').toUpperCase();
    if (r === 'CRITICAL') return COLORS.critical;
    if (r === 'HIGH') return COLORS.danger;
    if (r === 'MODERATE') return COLORS.warning;
    if (r === 'LOW') return COLORS.success;
    return COLORS.secondary;
  };

  const delta = (a, b) => {
    const d = (b || 0) - (a || 0);
    if (d === 0) return '  0';
    return d > 0 ? `+${d}%` : `${d}%`;
  };

  // ===== COVER =====
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('DefencIQ', margin, 20);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Assessment Comparison Report', margin, 30);
  doc.setFontSize(9);
  doc.text(`Generated ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`, pageWidth - margin, 30, { align: 'right' });

  yPos = 45;

  // ===== CLIENT HEADERS =====
  const leftX = margin;
  const rightX = margin + colWidth + 10;
  const colColors = [[59, 130, 246], [16, 185, 129]]; // blue, green

  [a1, a2].forEach((a, i) => {
    const x = i === 0 ? leftX : rightX;
    doc.setFillColor(...colColors[i]);
    doc.rect(x, yPos, colWidth, 3, 'F');
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, yPos + 3, colWidth, 22, 0, 0, 'F');
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(a.client_name || 'Untitled', x + 5, yPos + 13);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.secondary);
    doc.text(`${a.user_name || 'Unknown'} | ${new Date(a.created_at).toLocaleDateString('en-AU')} | ${(a.status || '').replace('_', ' ')}`, x + 5, yPos + 21);
  });
  yPos += 32;

  // ===== OVERALL SCORE =====
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Overall Score', margin, yPos);
  yPos += 6;
  drawLine(yPos);
  yPos += 6;

  [s1, s2].forEach((s, i) => {
    const x = i === 0 ? leftX : rightX;
    const scoreColor = getEnhancedBandColor(s?.finalScore || 0);
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text(`${s?.finalScore ?? 0}%`, x + 5, yPos + 10);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...scoreColor);
    doc.text(s?.band || 'N/A', x + 35, yPos + 10);
    if (i === 1) {
      const d = delta(s1?.finalScore, s2?.finalScore);
      const dColor = d.startsWith('+') ? COLORS.success : d.startsWith('-') ? COLORS.danger : COLORS.secondary;
      doc.setTextColor(...dColor);
      doc.text(d, x + colWidth - 5, yPos + 10, { align: 'right' });
    }
    drawBar(x + 5, yPos + 15, colWidth - 10, s?.finalScore || 0, colColors[i]);
  });
  yPos += 22;

  // Audit risk
  yPos += 4;
  [s1, s2].forEach((s, i) => {
    const x = i === 0 ? leftX : rightX;
    const rc = getRiskColor(s?.auditRisk);
    doc.setFillColor(...rc);
    doc.setGlobalAlpha && doc.setGState && false;
    doc.setTextColor(...rc);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${(s?.auditRisk || 'N/A').toUpperCase()} AUDIT RISK`, x + 5, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.secondary);
    (s?.reasons || []).slice(0, 3).forEach((r, ri) => {
      doc.text(`- ${r}`, x + 7, yPos + 5 + ri * 4);
    });
  });
  yPos += 20;

  // ===== SCORE COMPONENTS =====
  addNewPageIfNeeded(50);
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Score Components', margin, yPos);
  yPos += 6;
  drawLine(yPos);
  yPos += 8;

  Object.entries(SECTION_GROUPS).forEach(([key, group]) => {
    addNewPageIfNeeded(20);
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${key} (${Math.round(group.weight * 100)}% weight)`, margin, yPos);
    yPos += 5;

    [s1, s2].forEach((s, i) => {
      const x = i === 0 ? leftX : rightX;
      const val = Math.round(s?.groupScores?.[key] ?? 0);
      doc.setTextColor(...COLORS.primary);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${val}%`, x + 5, yPos);
      drawBar(x + 20, yPos - 3, colWidth - 30, val, colColors[i]);
      if (i === 1) {
        const d = delta(Math.round(s1?.groupScores?.[key] ?? 0), val);
        const dColor = d.startsWith('+') ? COLORS.success : d.startsWith('-') ? COLORS.danger : COLORS.secondary;
        doc.setTextColor(...dColor);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(d, x + colWidth - 5, yPos, { align: 'right' });
      }
    });
    yPos += 10;
  });

  // ===== DOMAIN BREAKDOWN =====
  addNewPageIfNeeded(40);
  yPos += 5;
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Domain Breakdown', margin, yPos);
  yPos += 6;
  drawLine(yPos);
  yPos += 4;

  // Table header
  const tableData = [];
  const domainSections = SECTION_ORDER.filter(id => id !== 'engagement');

  domainSections.forEach(sectionId => {
    const config = SECTION_CONFIG[sectionId];
    if (!config) return;
    const sc1 = Math.round(s1?.domainScores?.[sectionId]?.percentage ?? 0);
    const sc2 = Math.round(s2?.domainScores?.[sectionId]?.percentage ?? 0);
    const d = sc2 - sc1;
    const dStr = d === 0 ? '0' : d > 0 ? `+${d}%` : `${d}%`;
    tableData.push([config.title, `${sc1}%`, `${sc2}%`, dStr]);
  });

  if (tableData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      margin: { left: margin, right: margin },
      head: [['Domain', a1.client_name, a2.client_name, 'Delta']],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 3, textColor: COLORS.primary },
      headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontSize: 8, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 25, halign: 'center' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) {
          const val = data.cell.raw;
          if (val.startsWith('+')) data.cell.styles.textColor = COLORS.success;
          else if (val.startsWith('-')) data.cell.styles.textColor = COLORS.danger;
          else data.cell.styles.textColor = COLORS.secondary;
        }
      }
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.secondary);
    doc.text(`DefencIQ Comparison Report | Page ${p} of ${pageCount} | Confidential`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  }

  return doc;
};

/**
 * Export comparison PDF
 */
export const exportComparisonPDF = (a1, a2, s1, s2) => {
  const doc = generateComparisonPDF(a1, a2, s1, s2);
  const name1 = (a1.client_name || 'a').toLowerCase().replace(/[^a-z0-9]/g, '-').substring(0, 20);
  const name2 = (a2.client_name || 'b').toLowerCase().replace(/[^a-z0-9]/g, '-').substring(0, 20);
  const date = new Date().toISOString().split('T')[0];
  doc.save(`defenciq-comparison-${name1}-vs-${name2}-${date}.pdf`);
};


export default { generatePDFReport, exportPDF };
